import { useMemo, useState } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from 'react-simple-maps';
import { useRankingsNetworkSummaryQuery } from '../api/queries';
import type { RankingsMapPoint } from '../types';

// us-atlas states-10m, served from /public so it bundles with the app.
const GEO_URL = '/us-states-10m.json';

type Props = {
  onDrillIn: (afNumber: string) => void;
  /** When set, only show dots for franchisees assigned to this FBC. */
  fbcFilter?: string | null;
  title?: string;
};

// Grid-coverage → fill color. A location is blue if it appears in the Maps
// 3-pack anywhere across its geo-grid (any keyword); darker = broader
// coverage. In the grid but ranking nowhere = red. No grid data yet = grey
// (unknown — deliberately NOT red, to avoid false negatives).
type Band = 'none' | 'red' | 'low' | 'mid' | 'high';
const BAND_COLOR: Record<Band, string> = {
  none: '#cbd5e1',
  red: '#dc2626',
  low: '#93c5fd',
  mid: '#2563eb',
  high: '#1e3a8a',
};
function coverageBand(p: RankingsMapPoint): Band {
  if (!p.hasGrid || p.gridTotalPoints === 0) return 'none';
  const frac = p.gridCoveredPoints / p.gridTotalPoints;
  if (frac === 0) return 'red';
  if (frac < 0.34) return 'low';
  if (frac < 0.67) return 'mid';
  return 'high';
}
function colorFor(p: RankingsMapPoint): string {
  return BAND_COLOR[coverageBand(p)];
}

function pointKey(p: RankingsMapPoint, jitter: number): { x: number; y: number } {
  // Sub-pixel jitter so co-located AFs (shared office, same zip) don't
  // collapse into a single dot. Deterministic from afNumber so the
  // dot doesn't move between renders.
  const hash = [...p.afNumber].reduce((a, c) => a + c.charCodeAt(0), 0);
  const dx = ((hash % 17) - 8) / 17;
  const dy = (((hash * 31) % 17) - 8) / 17;
  return { x: p.lng + dx * jitter, y: p.lat + dy * jitter };
}

export default function NetworkRankingsMap({
  onDrillIn,
  fbcFilter,
  title = 'Grid Search Coverage — Network Map',
}: Props) {
  const { data, isLoading, error } = useRankingsNetworkSummaryQuery(true);
  const [hover, setHover] = useState<{ p: RankingsMapPoint; x: number; y: number } | null>(
    null,
  );

  const points = useMemo(() => {
    const all = data?.mapPoints ?? [];
    return fbcFilter ? all.filter((p) => p.fbcName === fbcFilter) : all;
  }, [data?.mapPoints, fbcFilter]);

  const buckets = useMemo(() => {
    const b: Record<Band, number> = { high: 0, mid: 0, low: 0, red: 0, none: 0 };
    for (const p of points) b[coverageBand(p)]++;
    return b;
  }, [points]);

  if (isLoading) {
    return (
      <>
        <div className="ds-section-header">
          <h2>{title}</h2>
          <span className="meta">Loading…</span>
        </div>
      </>
    );
  }
  if (error) {
    return (
      <>
        <div className="ds-section-header">
          <h2>{title}</h2>
          <span className="meta" style={{ color: '#dc2626' }}>
            Failed to load: {(error as Error).message}
          </span>
        </div>
      </>
    );
  }
  if (points.length === 0) return null;

  return (
    <>
      <div className="ds-section-header">
        <h2>Grid Search Coverage — Network Map</h2>
        <span className="meta">
          {points.length} locations · color = share of geo-grid where in the Maps 3-pack · click a dot to drill in
        </span>
      </div>
      <div
        className="ds-panel"
        style={{ padding: 16, position: 'relative' }}
        onMouseLeave={() => setHover(null)}
      >
        <ComposableMap
          projection="geoAlbersUsa"
          projectionConfig={{ scale: 1000 }}
          width={975}
          height={610}
          style={{ width: '100%', height: 'auto', display: 'block' }}
        >
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill="#f1f5f9"
                  stroke="#cbd5e1"
                  strokeWidth={0.5}
                  style={{
                    default: { outline: 'none' },
                    hover: { fill: '#e2e8f0', outline: 'none' },
                    pressed: { outline: 'none' },
                  }}
                />
              ))
            }
          </Geographies>
          {points.map((p) => {
            const jittered = pointKey(p, 0.35);
            return (
              <Marker
                key={p.afNumber}
                coordinates={[jittered.x, jittered.y]}
                onClick={() => onDrillIn(p.afNumber)}
                onMouseEnter={(e) => {
                  const rect = (
                    e.currentTarget.ownerSVGElement!.parentElement as HTMLElement
                  ).getBoundingClientRect();
                  setHover({
                    p,
                    x: e.clientX - rect.left,
                    y: e.clientY - rect.top,
                  });
                }}
                onMouseMove={(e) => {
                  const rect = (
                    e.currentTarget.ownerSVGElement!.parentElement as HTMLElement
                  ).getBoundingClientRect();
                  setHover((h) =>
                    h ? { ...h, x: e.clientX - rect.left, y: e.clientY - rect.top } : h,
                  );
                }}
                style={{ default: { cursor: 'pointer' } }}
              >
                <circle
                  r={4.5}
                  fill={colorFor(p)}
                  fillOpacity={0.85}
                  stroke="#fff"
                  strokeWidth={0.8}
                />
              </Marker>
            );
          })}
        </ComposableMap>

        {hover && (
          <div
            style={{
              position: 'absolute',
              left: Math.min(hover.x + 12, 600),
              top: Math.max(hover.y - 8, 0),
              pointerEvents: 'none',
              background: '#0f172a',
              color: '#fff',
              padding: '8px 10px',
              borderRadius: 6,
              fontSize: 12,
              lineHeight: 1.4,
              boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
              whiteSpace: 'nowrap',
              zIndex: 10,
            }}
          >
            <div style={{ fontWeight: 600 }}>{hover.p.name}</div>
            <div style={{ opacity: 0.8 }}>
              {hover.p.afNumber}
              {hover.p.location ? ` · ${hover.p.location}` : ''}
            </div>
            <div style={{ marginTop: 4 }}>
              {hover.p.hasGrid ? (
                <>
                  Grid: <strong>{hover.p.gridCoveredPoints}/{hover.p.gridTotalPoints}</strong> points in pack
                  {hover.p.gridBestRank != null && <> · best #{hover.p.gridBestRank}</>}
                </>
              ) : (
                <span style={{ opacity: 0.8 }}>No grid data yet</span>
              )}
            </div>
          </div>
        )}

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            justifyContent: 'center',
            marginTop: 12,
            fontSize: 12,
            color: '#475569',
            flexWrap: 'wrap',
          }}
        >
          <LegendDot color={BAND_COLOR.high} label={`Broad coverage (${buckets.high})`} />
          <LegendDot color={BAND_COLOR.mid} label={`Moderate (${buckets.mid})`} />
          <LegendDot color={BAND_COLOR.low} label={`Some (${buckets.low})`} />
          <LegendDot color={BAND_COLOR.red} label={`Not in grid (${buckets.red})`} />
          {buckets.none > 0 && <LegendDot color={BAND_COLOR.none} label={`No grid data (${buckets.none})`} />}
        </div>
      </div>
    </>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span
        style={{
          display: 'inline-block',
          width: 10,
          height: 10,
          borderRadius: '50%',
          background: color,
          border: '1px solid #fff',
          boxShadow: '0 0 0 1px #cbd5e1',
        }}
      />
      {label}
    </span>
  );
}
